import React from 'react';
import { Box, Button, Chip, Divider, Link as MuiLink, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { StudentProfileResponse } from '../../../services/studentProfileService';
import { useTimezone } from '../../../hooks/useTimezone';
import { InfoField, InfoGrid, SectionCard } from '../../common/SectionCard';
import PersonAvatar from '../../common/PersonAvatar';
import StatusChip, { ToneChip } from '../../common/StatusChip';
import { FAMILY_STATUS, familyName } from '../../families/familyFormat';
import ProfileTimeline from './ProfileTimeline';
import { ProfileEvent } from './profileEvents';
import { currentEnrollment, placementName } from './enrollmentPlacement';
import { ProfileTab } from './profileNav';
import { grLabel, profilePath } from '../../../utils/personRef';

interface Props {
  data: StudentProfileResponse;
  events: ProfileEvent[];
  onOpenTab: (tab: ProfileTab) => void;
}

const GENDER_LABEL: Record<string, string> = { male: 'Male', female: 'Female', other: 'Other' };

/** Who this is, who their family is, where they are now, and what happened lately. */
const StudentOverviewTab: React.FC<Props> = ({ data, events, onOpenTab }) => {
  const navigate = useNavigate();
  const { formatDate } = useTimezone();
  const { person, family, guardians, siblings, enrollments, admissions } = data;

  const current = currentEnrollment(enrollments);
  const latestApplication = admissions[0];
  const familyStatus = family ? FAMILY_STATUS[family.status] : null;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 2.5, alignItems: 'start' }}>
      <Stack spacing={2.5} sx={{ minWidth: 0 }}>
        <SectionCard title="Personal details">
          <InfoGrid>
            <InfoField label="Gender" value={GENDER_LABEL[person.gender]} />
            <InfoField label="Date of birth" value={person.dob ? formatDate(person.dob) : undefined} />
            <InfoField label="CNIC / B-Form" value={person.cnic} mono />
            <InfoField label="Phone" value={person.phone} />
            <InfoField label="Email" value={person.email} />
            {person.nameUrdu && <InfoField label="Name in Urdu" value={person.nameUrdu} />}
          </InfoGrid>
        </SectionCard>

        <SectionCard
          title="Family"
          action={familyStatus && <ToneChip label={familyStatus.label} tone={familyStatus.tone} />}
        >
          {!family ? (
            <Typography variant="body2" color="text.secondary">Not linked to a family yet.</Typography>
          ) : (
            <Stack spacing={1.75}>
              <Typography variant="subtitle2" fontWeight={700}>{familyName(family)}</Typography>
              {guardians.length === 0 && (
                <Typography variant="body2" color="text.secondary">No parents on record.</Typography>
              )}
              {guardians.map(g => (
                <Stack key={g._id} direction="row" spacing={1.5} alignItems="center">
                  <PersonAvatar name={g.name} size={36} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{g.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {g.relation}
                      {g.cnic && !g.cnicIsPlaceholder && <Box component="span" sx={{ fontFamily: 'monospace', ml: 1 }}>{g.cnic}</Box>}
                    </Typography>
                  </Box>
                  {g.phone && (
                    <MuiLink href={`tel:${g.phone}`} underline="hover" variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                      {g.phone}
                    </MuiLink>
                  )}
                </Stack>
              ))}
              {siblings.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {siblings.length === 1 ? 'Sibling' : `Siblings (${siblings.length})`}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                      {siblings.map(s => (
                        <Chip
                          key={s._id}
                          avatar={<PersonAvatar name={s.name} photoFilename={s.photoFilename} size={24} />}
                          label={`${s.name} · ${grLabel(s)}`}
                          variant="outlined"
                          onClick={() => navigate(profilePath(s))}
                          sx={{ fontWeight: 500 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </SectionCard>
      </Stack>

      <Stack spacing={2.5} sx={{ minWidth: 0 }}>
        <SectionCard title="Current enrollment">
          {current ? (
            <Stack spacing={1.75}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="h6" fontWeight={700}>{placementName(current) || 'Class not set'}</Typography>
                <StatusChip status={{ kind: 'enrollment', value: current.enrollmentStatus }} />
              </Stack>
              <InfoGrid>
                <InfoField label="Session" value={current.sessionId?.name} />
                <InfoField label="Roll number" value={current.rollNumber != null ? String(current.rollNumber) : undefined} />
                <InfoField label="Enrolled on" value={current.enrolledOn ? formatDate(current.enrolledOn) : undefined} />
              </InfoGrid>
            </Stack>
          ) : (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">Not enrolled in a class.</Typography>
              {latestApplication && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">Latest application</Typography>
                  <StatusChip status={{ kind: 'application', value: latestApplication.status }} />
                </Stack>
              )}
            </Stack>
          )}
        </SectionCard>

        <SectionCard
          title="Recent activity"
          action={events.length > 5 && (
            <Button size="small" onClick={() => onOpenTab('timeline')} sx={{ textTransform: 'none', my: -0.5 }}>
              View all
            </Button>
          )}
        >
          {events.length === 0
            ? <Typography variant="body2" color="text.secondary">Nothing recorded yet.</Typography>
            : <ProfileTimeline events={events.slice(0, 5)} />}
        </SectionCard>
      </Stack>
    </Box>
  );
};

export default StudentOverviewTab;
